/**
 * Runtime environment variable validation.
 *
 * IMPORTANT: Next.js only inlines NEXT_PUBLIC_* vars when accessed as
 * literal `process.env.NEXT_PUBLIC_X` — dynamic access like
 * `process.env[key]` returns undefined on the client.
 * That's why each getter references the full literal string.
 */
function requireEnv(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(
      `Missing required environment variable: ${name}. ` +
        `Check your .env.local or deployment configuration.`
    );
  }
  return value;
}

/**
 * Runtime guard for server-only secrets (PResto #51).
 *
 * Defense-in-depth: even though Next.js will not inline non-`NEXT_PUBLIC_`
 * variables into the client bundle (so the value would be `undefined` and
 * `requireEnv` would already throw with "missing var" in the browser), an
 * explicit window check produces a clearer error and catches accidental
 * imports of secrets into client components before they ship.
 *
 * Note: we do NOT use `import "server-only"` because this module also
 * exposes the public Supabase keys (URL / anon key), which legitimately
 * need to be imported from browser code (`apps/ui/lib/supabase/client.ts`).
 */
function assertServer(name: string): void {
  if (typeof window !== "undefined") {
    throw new Error(
      `${name} is server-only and must not be accessed in the browser`,
    );
  }
}

export const env = {
  get SUPABASE_URL() {
    return requireEnv(process.env.NEXT_PUBLIC_SUPABASE_URL, "NEXT_PUBLIC_SUPABASE_URL");
  },
  get SUPABASE_ANON_KEY() {
    return requireEnv(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY, "NEXT_PUBLIC_SUPABASE_ANON_KEY");
  },
  /**
   * Trigger.dev secret key — server-side only (no NEXT_PUBLIC_ prefix).
   * Only call from API routes or server components; accessing on the client
   * will throw because Next.js does not expose non-public env vars there.
   */
  get TRIGGER_SECRET_KEY() {
    assertServer("TRIGGER_SECRET_KEY");
    return requireEnv(process.env.TRIGGER_SECRET_KEY, "TRIGGER_SECRET_KEY");
  },
  /**
   * Trigger.dev API base URL (e.g. https://trigger.atrvd.com).
   * Server-side only — used alongside TRIGGER_SECRET_KEY in API routes.
   */
  get TRIGGER_API_URL() {
    assertServer("TRIGGER_API_URL");
    return requireEnv(process.env.TRIGGER_API_URL, "TRIGGER_API_URL");
  },
  /** Supabase service-role key — server-side only. Bypasses RLS for oauth_tokens etc. */
  get SUPABASE_SERVICE_ROLE_KEY() {
    assertServer("SUPABASE_SERVICE_ROLE_KEY");
    return requireEnv(process.env.SUPABASE_SERVICE_ROLE_KEY, "SUPABASE_SERVICE_ROLE_KEY");
  },
  /** ZoomInfo OAuth2 client ID — server-side only. */
  get ZOOMINFO_CLIENT_ID() {
    assertServer("ZOOMINFO_CLIENT_ID");
    return requireEnv(process.env.ZOOMINFO_CLIENT_ID, "ZOOMINFO_CLIENT_ID");
  },
  /** ZoomInfo OAuth2 client secret — server-side only. */
  get ZOOMINFO_CLIENT_SECRET() {
    assertServer("ZOOMINFO_CLIENT_SECRET");
    return requireEnv(process.env.ZOOMINFO_CLIENT_SECRET, "ZOOMINFO_CLIENT_SECRET");
  },
  /**
   * Timeout for each Trigger.dev API call (ms). Defaults to 10s.
   * Override via TRIGGER_TIMEOUT_MS env when ops needs a different value
   * (e.g. degraded Trigger.dev region or larger batch enrichment runs).
   */
  get TRIGGER_TIMEOUT_MS(): number {
    const raw = process.env.TRIGGER_TIMEOUT_MS;
    if (!raw) return 10_000;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 1000) {
      throw new Error(
        `Invalid TRIGGER_TIMEOUT_MS=${raw} — must be an integer >= 1000`,
      );
    }
    return parsed;
  },
  /**
   * TTL (hours) for enrichment-related Trigger.dev runs. Defaults to 168 (7d).
   * Override via TRIGGER_ENRICH_TTL_HOURS env when ops needs a different
   * retention policy (e.g. 24h during incidents to drop stale work fast,
   * 720h for audit-heavy environments). `triggerCompanyPipeline` reads this
   * getter per-call so flips take effect without a redeploy.
   */
  get TRIGGER_ENRICH_TTL_HOURS(): number {
    const raw = process.env.TRIGGER_ENRICH_TTL_HOURS;
    if (!raw) return 168;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      throw new Error(
        `Invalid TRIGGER_ENRICH_TTL_HOURS=${raw} — must be a positive integer (hours)`,
      );
    }
    return parsed;
  },
} as const;

/**
 * True when every secret the ZoomInfo OAuth routes need is set: the client
 * credentials for the token exchange and the service-role key for the
 * oauth_tokens table. Lets connect/callback/status answer "not configured"
 * instead of throwing from the `env` getters.
 */
export function isZoomInfoConfigured(): boolean {
  assertServer("isZoomInfoConfigured");
  return Boolean(process.env.ZOOMINFO_CLIENT_ID && process.env.ZOOMINFO_CLIENT_SECRET && process.env.SUPABASE_SERVICE_ROLE_KEY);
}
